import {
  adminApiClient,
  extractDownloadFilename,
  resolveApiErrorMessage,
  resolveBlobApiErrorMessage,
} from '@/services/admin/baseService'
import type { NetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { buildModeAwareConfig } from '@/services/admin/modeAware'
import { evmOperationService } from '@/services/admin/evmOperationService'
import { solanaOperationService } from '@/services/admin/solanaOperationService'
import { toBooleanValue } from '@/services/admin/valueHelpers'
import type {
  ApiResponse,
  BatchVerifyRequest,
  EvmOperationEnvelope,
  EvmPendingOperationItem,
  MetaRefundRequestPayload,
  MetaRefundResponse,
  OrderRefundData,
  OrderRefundHistory,
  PaginatedResponse,
  RefundOperation,
  RefundOperationListParams,
  RefundOperationVerifyRequest,
  RefundStatistics,
  SolanaOperationEnvelope,
  SolanaPendingMetaRefundItem,
} from '@/types'

const toStringSafe = (value: unknown): string | undefined => {
  if (value === null || value === undefined) {
    return undefined
  }
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed.length > 0 ? trimmed : undefined
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return `${value}`
  }
  return undefined
}

export class AdminRefundServiceError extends Error {
  readonly code?: string
  readonly status?: number

  constructor(message: string, options: { code?: string; status?: number } = {}) {
    super(message)
    this.name = 'AdminRefundServiceError'
    this.code = options.code
    this.status = options.status
  }
}

export interface RefundVerificationTriggerRequest {
  merchantId?: string
  orderIds?: string[]
  chainId?: string
  dateFrom?: string
  dateTo?: string
  force?: boolean
}

export interface RefundVerificationTriggerResults {
  refundsFound: number
  refundsQueued: number
  refundsSkipped: number
  queueErrors: number
}

export interface RefundVerificationTriggerResponse {
  success: true
  message: string
  results: RefundVerificationTriggerResults
}

interface RefundVerificationTriggerErrorResponse {
  success: false
  error?: string
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object'

const resolveApiErrorCode = (error: unknown): string | undefined => {
  if (!isRecord(error)) {
    return undefined
  }
  const response = isRecord(error.response) ? error.response : undefined
  const data = isRecord(response?.data) ? response.data : undefined
  const nestedError = isRecord(data?.error) ? data.error : undefined
  const nestedCode = toStringSafe(nestedError?.code)
  if (nestedCode) {
    return nestedCode
  }
  return toStringSafe(data?.code)
}

const resolveApiErrorStatus = (error: unknown): number | undefined => {
  if (!isRecord(error) || !isRecord(error.response)) {
    return undefined
  }
  const status = Number(error.response.status)
  return Number.isFinite(status) ? status : undefined
}

const createAdminRefundServiceError = (error: unknown, fallback: string): AdminRefundServiceError =>
  new AdminRefundServiceError(resolveApiErrorMessage(error, fallback), {
    code: resolveApiErrorCode(error),
    status: resolveApiErrorStatus(error),
  })

const toNumberOrUndefined = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined
  }
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

const normalizeRefundStatusId = (status: unknown): RefundOperation['status'] => {
  const raw = toStringSafe(status)
  if (!raw) {
    return 'ros_pending'
  }
  const normalized = raw.trim()
  if (normalized.startsWith('ros_')) {
    switch (normalized as RefundOperation['status']) {
      case 'ros_pending':
      case 'ros_refunded':
      case 'ros_failed':
        return normalized as RefundOperation['status']
      default:
        break
    }
  }
  switch (normalized.toLowerCase()) {
    case 'pending':
    case 'queued':
      return 'ros_pending'
    case 'refunded':
    case 'completed':
      return 'ros_refunded'
    case 'failed':
    case 'error':
      return 'ros_failed'
    default:
      return 'ros_pending'
  }
}

const parseRefundMetaEntries = (meta: unknown): Array<{ recipient: string; amount: string }> => {
  if (!meta) {
    return []
  }
  if (Array.isArray(meta)) {
    return meta
      .map((entry: any) => ({
        recipient: toStringSafe(entry?.recipient) ?? toStringSafe(entry?.to_address) ?? '',
        amount: toStringSafe(entry?.amount) ?? '0',
      }))
      .filter(entry => entry.recipient)
  }
  if (typeof meta === 'string') {
    try {
      const parsed = JSON.parse(meta)
      return parseRefundMetaEntries(parsed)
    } catch {
      return []
    }
  }
  return []
}

const parseVerificationNotes = (
  notes: unknown
): RefundOperation['verification_notes'] | undefined => {
  if (!notes) {
    return undefined
  }

  if (Array.isArray(notes)) {
    return notes
      .map((entry: any) => {
        const timestamp =
          toStringSafe(entry?.timestamp) ??
          toStringSafe(entry?.createdAt) ??
          toStringSafe(entry?.created_at) ??
          ''
        const type =
          toStringSafe(entry?.type) ??
          (toStringSafe(entry?.result)?.includes('failure') ? 'failed' : 'info')
        const note =
          toStringSafe(entry?.note) ??
          toStringSafe(entry?.reason) ??
          toStringSafe(entry?.error) ??
          toStringSafe(entry?.result) ??
          JSON.stringify(entry)

        return { timestamp, type, note }
      })
      .filter(entry => entry.note)
  }

  const notesText = toStringSafe(notes)
  if (!notesText) {
    return undefined
  }

  try {
    const parsed = JSON.parse(notesText)
    const parsedNotes = parseVerificationNotes(parsed)
    return parsedNotes ?? notesText
  } catch {
    return notesText
  }
}

const resolveChainIdentifier = (...candidates: Array<unknown>): string => {
  for (const candidate of candidates) {
    if (candidate === null || candidate === undefined) continue
    if (typeof candidate === 'number' && Number.isFinite(candidate)) {
      return `${candidate}`
    }
    const candidateString = toStringSafe(candidate)
    if (candidateString) {
      return candidateString
    }
  }
  return 'unknown'
}

const mapRefundOperation = (record: any): RefundOperation => {
  if (!record || typeof record !== 'object') {
    const now = new Date().toISOString()
    return {
      id: '',
      order_id: '',
      merchant_id: '',
      chain_id: 'unknown',
      token_address: '',
      refund_amount: '0',
      refund_amount_in_smallest_unit: '0',
      status: 'ros_pending',
      is_verified: false,
      created_at: now,
      updated_at: now,
    }
  }

  const orderInfo = record.order as Record<string, unknown> | undefined
  const orderBusinessId =
    toStringSafe(record.orderId) ??
    toStringSafe(record.order_id) ??
    toStringSafe(orderInfo?.orderId) ??
    ''
  const internalOrderId =
    toStringSafe(record.order_id) ??
    orderBusinessId

  const merchantId =
    toStringSafe(record.merchant_id) ??
    toStringSafe(orderInfo?.merchantId) ??
    ''
  const merchantName =
    toStringSafe(record.merchant_name) ??
    toStringSafe(record.merchantName) ??
    undefined

  const chainIdRaw = resolveChainIdentifier(
    record.chain_id,
    record.chainId,
    record.chain_name,
    record.chainName,
    record.chain_code,
    record.chainCode,
    record.registry_chain_id,
    record.registryChainId,
    record.chain_numeric_id,
    record.chainNumericId,
    orderInfo?.chainId,
    (orderInfo as any)?.chain_id,
  )

  const resolvedChainNumericId = toNumberOrUndefined(
    record.chain_numeric_id ??
      record.chainNumericId ??
      (orderInfo as any)?.chain_numeric_id ??
      (orderInfo as any)?.chainNumericId ??
      (typeof record.chainId === 'number' ? record.chainId : undefined) ??
      (typeof orderInfo?.chainId === 'number' ? orderInfo?.chainId : undefined)
  )

  let resolvedChainCode =
    toStringSafe(record.chain_code) ??
    toStringSafe(record.chainCode) ??
    toStringSafe((orderInfo as any)?.chain_code) ??
    toStringSafe((orderInfo as any)?.chainCode) ??
    undefined

  let chainId = chainIdRaw
  const stringCandidates = new Set<string>()
  const pushStringCandidate = (value?: string | null) => {
    const normalized = toStringSafe(value)
    if (!normalized) {
      return
    }
    stringCandidates.add(normalized)
    stringCandidates.add(normalized.toLowerCase())

    const withoutHyphen = normalized.replace(/-/gu, '_')
    stringCandidates.add(withoutHyphen)
    stringCandidates.add(withoutHyphen.toLowerCase())

    const lowerNormalized = normalized.toLowerCase()

    if (lowerNormalized.startsWith('chain-') || lowerNormalized.startsWith('chain_')) {
      const separatorIndex = normalized.indexOf('-')
      const tail = separatorIndex >= 0 ? normalized.slice(separatorIndex + 1).replace(/^_+/u, '') : ''
      if (tail && tail !== normalized) {
        pushStringCandidate(tail)
      }
    }
  }

  pushStringCandidate(resolvedChainCode)
  pushStringCandidate(chainIdRaw)
  pushStringCandidate(toStringSafe(record.chain_name) ?? toStringSafe(record.chainName))
  pushStringCandidate(record.chain_identifier)
  pushStringCandidate(record.chainIdentifier)
  pushStringCandidate(record.numericChainId)

  const ensureNumericSuffix = (value: string): string | undefined => {
    const match = value.match(/(\d+)$/u)
    return match ? match[1] : undefined
  }

  if (typeof resolvedChainNumericId === 'number') {
    chainId = `${resolvedChainNumericId}`
  } else if (!resolvedChainNumericId) {
    for (const candidate of stringCandidates) {
      const numericSuffix = ensureNumericSuffix(candidate)
      if (numericSuffix) {
        chainId = candidate
        break
      }
    }
  }

  const amountInSmallest = toStringSafe(record.refund_amount_in_smallest_unit) ?? toStringSafe(record.amount_in_smallest_unit)
  const totalRefundAmount = toStringSafe(record.total_refund_amount) ?? toStringSafe(record.totalRefundAmount)
  const refundAmount =
    toStringSafe(record.refund_amount) ??
    toStringSafe(record.amount) ??
    totalRefundAmount ??
    amountInSmallest ??
    '0'
  const formattedRefundAmount =
    toStringSafe(record.formatted_refund_amount) ?? toStringSafe(record.formattedRefundAmount)
  const refundAmountSmallest = amountInSmallest ?? totalRefundAmount ?? '0'

  const refundMeta = parseRefundMetaEntries(record.refund_meta ?? record.refundMeta)
  const refundMetaCount = toNumberOrUndefined(record.refund_meta_count ?? record.refundMetaCount)
  const refundTxHash = toStringSafe(record.refund_tx_hash) ?? toStringSafe(record.refundTxHash) ?? undefined
  const refundBlockNumber = toNumberOrUndefined(record.refund_block_number ?? record.refundBlockNumber)
  const refundGasUsed = toStringSafe(record.refund_gas_used ?? record.refundGasUsed) ?? undefined
  const verificationNotes = parseVerificationNotes(record.verification_notes ?? record.verificationNotes)
  const refundNotes = toStringSafe(record.refund_notes) ?? toStringSafe(record.refundNotes) ?? undefined
  const createdAt = toStringSafe(record.created_at) ?? toStringSafe(record.createdAt) ?? new Date().toISOString()
  const updatedAt = toStringSafe(record.updated_at) ?? toStringSafe(record.updatedAt) ?? createdAt
  const resolvedStatus = normalizeRefundStatusId(record.status)
  const chainName =
    toStringSafe(record.chain_name) ??
    toStringSafe(record.chainName) ??
    toStringSafe(orderInfo?.chainName) ??
    undefined

  const registryEntry = record.registry
  if (registryEntry && typeof registryEntry === 'object') {
    resolvedChainCode =
      resolvedChainCode ??
      toStringSafe((registryEntry as any).chain_code) ??
      toStringSafe((registryEntry as any).chainCode) ??
      undefined
  }

  const normalizedRecord: RefundOperation = {
    ...(record as Record<string, unknown>),
    id: toStringSafe(record.id) ?? toStringSafe(record.refundId) ?? '',
    order_id: internalOrderId,
    orderId: orderBusinessId,
    merchant_id: merchantId,
    merchant_name: merchantName,
    chain_id: chainId,
    chain_name: chainName,
    isTestnet: toBooleanValue(record.is_testnet ?? record.isTestnet, false),
    token_address: toStringSafe(record.token_address) ?? toStringSafe(record.tokenAddress) ?? '',
    token_symbol: toStringSafe(record.token_symbol) ?? toStringSafe(record.tokenSymbol) ?? 'UNKNOWN',
    token_decimals: toNumberOrUndefined(record.token_decimals ?? record.tokenDecimals),
    tokenDecimals: toNumberOrUndefined(record.tokenDecimals ?? record.token_decimals),
    refund_amount: refundAmount ?? '0',
    refund_amount_in_smallest_unit: refundAmountSmallest,
    formatted_refund_amount: formattedRefundAmount ?? refundAmount,
    total_refund_amount: totalRefundAmount ?? refundAmount,
    refund_meta_count: refundMetaCount,
    refund_meta: refundMeta,
    refund_tx_hash: refundTxHash,
    refund_block_number: refundBlockNumber,
    refund_gas_used: refundGasUsed,
    status: resolvedStatus,
    status_description: toStringSafe(record.status_description) ?? toStringSafe(record.statusDescription),
    is_verified: toBooleanValue(record.is_verified ?? record.isVerified, false),
    verified_at: toStringSafe(record.verified_at) ?? toStringSafe(record.verifiedAt),
    verification_notes: verificationNotes,
    verification_soft_retry_count: toNumberOrUndefined(
      record.verification_soft_retry_count ??
        record.verificationSoftRetryCount ??
        record.softRetryCount
    ),
    last_verification_check_at:
      toStringSafe(record.last_verification_check_at) ??
      toStringSafe(record.lastVerificationCheckAt) ??
      toStringSafe(record.lastAttemptedAt) ??
      undefined,
    refund_notes: refundNotes ?? undefined,
    refunded_by: toStringSafe(record.refund_by) ?? toStringSafe(record.refundBy) ?? undefined,
    error_message: toStringSafe(record.error_message) ?? toStringSafe(record.last_error) ?? toStringSafe(record.lastError),
    notes: toStringSafe(record.notes) ?? toStringSafe(record.statusDescription),
    created_at: createdAt,
    updated_at: updatedAt,
  }

  if (resolvedChainCode) {
    normalizedRecord.chain_code = resolvedChainCode
  }

  if (typeof resolvedChainNumericId === 'number') {
    normalizedRecord.chain_numeric_id = resolvedChainNumericId
  } else if (registryEntry && typeof (registryEntry as any).chainId === 'number') {
    normalizedRecord.chain_numeric_id = (registryEntry as any).chainId
  }

  return normalizedRecord
}

export const resolveCanonicalEvmRefundResourceId = (
  refundData: OrderRefundData | undefined
): string | null => {
  const resourceId = refundData?.internalOrderId
  if (
    refundData?.compatibility !== 'evm'
    || typeof resourceId !== 'string'
    || resourceId.length === 0
    || resourceId !== resourceId.trim()
    || resourceId !== resourceId.normalize('NFC')
    || [...resourceId].length > 256
    || [...resourceId].some(character => {
      const codePoint = character.codePointAt(0)
      return codePoint !== undefined && (codePoint <= 0x1f || codePoint === 0x7f)
    })
  ) {
    return null
  }
  return resourceId
}

export const resolveCanonicalSolanaRefundResourceId = (
  refundData: OrderRefundData | undefined
): string | null => {
  const resourceId = refundData?.internalOrderId
  if (
    refundData?.compatibility !== 'solana'
    || typeof resourceId !== 'string'
    || resourceId.length === 0
    || resourceId !== resourceId.trim()
    || resourceId !== resourceId.normalize('NFC')
    || new TextEncoder().encode(resourceId).byteLength > 200
    || [...resourceId].some(character => {
      const codePoint = character.codePointAt(0)
      return codePoint !== undefined && (codePoint <= 0x1f || codePoint === 0x7f)
    })
  ) {
    return null
  }
  return resourceId
}

export const refundService = {
  resolveCanonicalEvmRefundResourceId,
  resolveCanonicalSolanaRefundResourceId,

  createEvmMetaRefundOperation(
    adminId: string,
    orderId: string,
    resourceId: string,
    payload: MetaRefundRequestPayload,
    mode: NetworkEnvironment
  ): EvmPendingOperationItem {
    return evmOperationService.createPending(adminId, mode, {
      operationType: 'evm_platform_meta_refund',
      requestBody: payload,
      clientResourceLocator: { orderId },
      resourceType: 'refund_order',
      resourceId,
    })
  },

  async submitEvmMetaRefundOperation(
    adminId: string,
    item: EvmPendingOperationItem,
    mode: NetworkEnvironment
  ): Promise<EvmOperationEnvelope> {
    return evmOperationService.submitPending(adminId, mode, item)
  },

  createSolanaMetaRefundOperation(
    adminId: string,
    orderId: string,
    resourceId: string,
    payload: MetaRefundRequestPayload,
    mode: NetworkEnvironment
  ): SolanaPendingMetaRefundItem {
    return solanaOperationService.createOrReusePending(
      adminId,
      mode,
      orderId,
      resourceId,
      payload
    )
  },

  async submitSolanaMetaRefundOperation(
    adminId: string,
    item: SolanaPendingMetaRefundItem,
    mode: NetworkEnvironment
  ): Promise<SolanaOperationEnvelope> {
    return solanaOperationService.submitPending(adminId, mode, item)
  },

  async exportCsv(
    params?: RefundOperationListParams,
    mode?: NetworkEnvironment
  ): Promise<{ blob: Blob; filename: string }> {
    try {
      const response = await adminApiClient.get<Blob>('/admin/refunds/export', {
        params: {
          search: params?.search,
          status: params?.status,
          chainId: params?.chain_id,
          merchantId: params?.merchant_id,
          isVerified:
            params?.is_verified === undefined ? undefined : params.is_verified ? 'true' : 'false',
          dateFrom: params?.date_from,
          dateTo: params?.date_to,
          sortBy: params?.sortBy,
          sortOrder: params?.sortOrder,
        },
        responseType: 'blob',
        ...buildModeAwareConfig(mode),
      })

      return {
        blob: response.data,
        filename: extractDownloadFilename(
          response.headers['content-disposition'],
          `refund-operations-${new Date().toISOString().slice(0, 10)}.csv`
        ),
      }
    } catch (error) {
      throw new Error(await resolveBlobApiErrorMessage(error, 'Failed to export refund operations'))
    }
  },

  async getAllRefundOperations(
    params?: RefundOperationListParams,
    mode?: NetworkEnvironment
  ): Promise<PaginatedResponse<RefundOperation>> {
    const searchParams = new URLSearchParams()

    if (params?.page) searchParams.append('page', params.page.toString())
    if (params?.limit) searchParams.append('limit', params.limit.toString())
    if (params?.search) searchParams.append('search', params.search)
    if (params?.status) searchParams.append('status', params.status)
    if (params?.chain_id) searchParams.append('chainId', params.chain_id)
    if (params?.merchant_id) searchParams.append('merchantId', params.merchant_id)
    if (params?.is_verified !== undefined) searchParams.append('isVerified', params.is_verified ? 'true' : 'false')
    if (params?.date_from) searchParams.append('dateFrom', params.date_from)
    if (params?.date_to) searchParams.append('dateTo', params.date_to)
    if (params?.sortBy) searchParams.append('sortBy', params.sortBy)
    if (params?.sortOrder) searchParams.append('sortOrder', params.sortOrder)

    const query = searchParams.toString()
    const url = query ? `/admin/refunds?${query}` : '/admin/refunds'
    const response = await adminApiClient.get<ApiResponse<any>>(url, buildModeAwareConfig(mode))

    const success = Boolean(response.data?.success)
    const items =
      (response.data?.data as any)?.refunds ??
      (response.data as any)?.refunds ??
      (response.data as any)?.data ??
      []

    const paginationSource =
      (response.data as any)?.pagination ??
      (response.data as any)?.data?.pagination ??
      null

    const normalizePagination = (
      rawPagination: any,
      defaults: { page?: number; limit?: number; total?: number } = {}
    ) => {
      const page = toNumberOrUndefined(rawPagination?.page) ?? defaults.page ?? 1
      const limit = toNumberOrUndefined(rawPagination?.limit) ?? defaults.limit ?? 20
      const total = toNumberOrUndefined(rawPagination?.total) ?? defaults.total ?? 0
      const totalPagesRaw = rawPagination?.totalPages ?? rawPagination?.total_pages ?? rawPagination?.pages
      const pages = Number.isFinite(totalPagesRaw)
        ? Number(totalPagesRaw)
        : limit > 0
          ? Math.ceil(total / limit)
          : 0

      return {
        page,
        limit,
        total,
        pages,
        total_pages: rawPagination?.total_pages ?? pages,
      }
    }

    const pagination = normalizePagination(paginationSource, {
      page: params?.page,
      limit: params?.limit,
      total: Array.isArray(items) ? items.length : 0,
    })

    const mapped = Array.isArray(items) ? items.map(mapRefundOperation) : []
    return {
      success,
      data: mapped,
      error: success ? undefined : toStringSafe((response.data as any)?.error),
      message: (response.data as any)?.message,
      pagination,
    }
  },

  async getRefundOperationById(id: string, mode?: NetworkEnvironment): Promise<RefundOperation> {
    const response = await adminApiClient.get<ApiResponse<any>>(
      `/admin/refunds/${id}`,
      buildModeAwareConfig(mode)
    )

    if (!response.data?.data) {
      throw new Error('Failed to fetch refund operation')
    }

    const payload = response.data.data as any
    return mapRefundOperation(payload.refund ?? payload)
  },

  async verifyRefundOperation(
    id: string,
    data?: RefundOperationVerifyRequest,
    mode?: NetworkEnvironment
  ): Promise<ApiResponse<RefundOperation>> {
    const response = await adminApiClient.post<ApiResponse<RefundOperation>>(
      `/admin/refunds/${id}/verify`,
      data || {},
      buildModeAwareConfig(mode)
    )

    if (!response.data) {
      throw new Error('Failed to verify refund operation')
    }

    if (response.data.data) {
      return {
        ...response.data,
        data: mapRefundOperation(response.data.data),
      }
    }

    return response.data
  },

  async verifyRefundBatch(
    request: BatchVerifyRequest,
    mode?: NetworkEnvironment
  ): Promise<ApiResponse<{
    success: boolean
    message: string
    data: {
      batch_id: string
      refund_count: number
      status: string
      priority: 'normal' | 'high'
    }
  }>> {
    const response = await adminApiClient.post<ApiResponse<{
      success: boolean
      message: string
      data: {
        batch_id: string
        refund_count: number
        status: string
        priority: 'normal' | 'high'
      }
    }>>('/admin/refunds/verify-batch', request, buildModeAwareConfig(mode))

    if (!response.data) {
      throw new Error('Failed to trigger batch refund verification')
    }

    return response.data
  },

  async getOrderRefundData(
    orderId: string,
    mode?: NetworkEnvironment
  ): Promise<OrderRefundData> {
    const response = await adminApiClient.get<ApiResponse<OrderRefundData>>(
      `/admin/orders/${orderId}/refund-data`,
      buildModeAwareConfig(mode)
    ).catch((error: unknown) => {
      throw createAdminRefundServiceError(error, 'Failed to fetch order refund data')
    })

    if (!response.data?.data) {
      throw new Error('Failed to fetch order refund data')
    }

    return response.data.data
  },

  async getOrderRefundHistory(
    orderId: string,
    mode?: NetworkEnvironment
  ): Promise<OrderRefundHistory> {
    const response = await adminApiClient.get<ApiResponse<OrderRefundHistory>>(
      `/admin/orders/${orderId}/refund-history`,
      buildModeAwareConfig(mode)
    ).catch((error: unknown) => {
      throw createAdminRefundServiceError(error, 'Failed to fetch order refund history')
    })

    if (!response.data?.data) {
      throw new Error('Failed to fetch order refund history')
    }

    return response.data.data
  },

  async executeMetaRefund(
    orderId: string,
    payload: MetaRefundRequestPayload,
    mode?: NetworkEnvironment
  ): Promise<MetaRefundResponse> {
    const response = await adminApiClient.post<ApiResponse<MetaRefundResponse>>(
      `/admin/orders/${orderId}/meta-refund`,
      payload,
      buildModeAwareConfig(mode)
    )

    if (!response.data?.data) {
      throw new Error(response.data?.error || 'Meta refund execution failed')
    }

    const result = response.data.data
    return {
      ...result,
      success: result.success ?? true,
    }
  },

  async getRefundStatistics(mode?: NetworkEnvironment): Promise<ApiResponse<RefundStatistics>> {
    const response = await adminApiClient.get<ApiResponse<RefundStatistics>>(
      '/admin/refunds/statistics',
      buildModeAwareConfig(mode)
    )

    if (!response.data) {
      throw new Error('Failed to fetch refund statistics')
    }

    return response.data
  },

  async triggerRefundVerification(
    params: RefundVerificationTriggerRequest,
    mode?: NetworkEnvironment
  ): Promise<RefundVerificationTriggerResponse> {
    const response = await adminApiClient.post<
      RefundVerificationTriggerResponse | RefundVerificationTriggerErrorResponse
    >(
      '/admin/refunds/trigger-verification',
      params,
      buildModeAwareConfig(mode)
    )

    if (!response.data || response.data.success === false) {
      throw new Error(response.data?.error || 'Failed to trigger refund verification')
    }

    return response.data
  },
}
